import React from "react";
import { ChevronLeft, ChevronRight, Bell, Trophy, Gift, Sparkles } from "lucide-react";
import { motion } from "framer-motion";
import StatusBadge from "../StatusBadge";

export const PAGE = 8;

export const BANNERS = [
  "/assets/landing/dash_b1.webp",
  "/assets/landing/dash_b2.webp",
  "/assets/landing/dash_b3.webp",
];

export const inr = (p) => `₹${((p || 0) / 100).toLocaleString("en-IN")}`;

export const EmptyState = ({ icon: Icon, title, sub, testid }) => (
  <div data-testid={testid} className="ledger-card flex flex-col items-center justify-center rounded-2xl px-6 py-16 text-center">
    <div className="grid h-14 w-14 place-items-center rounded-2xl bg-white/5 text-slate-500"><Icon className="h-6 w-6" /></div>
    <p className="mt-4 font-heading text-lg font-bold">{title}</p>
    {sub && <p className="mt-1 max-w-xs text-sm text-slate-500">{sub}</p>}
  </div>
);

export const Pagination = ({ page, pages, onPage, testid }) => (
  pages > 1 ? (
    <div className="flex items-center justify-center gap-3 p-4" data-testid={testid}>
      <button data-testid={`${testid}-prev`} disabled={page <= 1} onClick={() => onPage(page - 1)} className="rounded-lg border border-slate-700 p-2 text-slate-300 disabled:opacity-40"><ChevronLeft className="h-4 w-4" /></button>
      <span className="font-mono text-xs text-slate-400">Page {page} / {pages}</span>
      <button data-testid={`${testid}-next`} disabled={page >= pages} onClick={() => onPage(page + 1)} className="rounded-lg border border-slate-700 p-2 text-slate-300 disabled:opacity-40"><ChevronRight className="h-4 w-4" /></button>
    </div>
  ) : null
);

export const StatCard = ({ label, value, accent, testid }) => (
  <div data-testid={testid} className="ledger-card rounded-2xl p-4">
    <p className="font-mono text-[10px] uppercase tracking-widest text-slate-500">{label}</p>
    <p className={`mt-1 font-heading text-xl font-extrabold ${accent ? "text-emerald-400" : "text-slate-100"}`}>{value}</p>
  </div>
);

export const TxTable = ({ txItems, filteredTx, txPage, txPages, setTxPage }) => (
  <div className="ledger-card overflow-hidden rounded-2xl">
    <div className="overflow-x-auto">
      <table className="w-full text-sm" data-testid="customer-transactions-table">
        <thead className="bg-slate-900/50 font-mono text-xs uppercase tracking-wider text-slate-500">
          <tr><th className="p-4 text-left">Date</th><th className="p-4 text-left">Type</th><th className="p-4 text-left">Ref</th><th className="p-4 text-right">Amount</th><th className="p-4 text-left">Status</th></tr>
        </thead>
        <tbody>
          {txItems.map((t) => (
            <tr key={t.id} className="border-t border-slate-800/60">
              <td className="p-4 text-slate-400">{new Date(t.date).toLocaleDateString("en-IN")}</td>
              <td className="p-4">{t.type}</td>
              <td className="p-4 font-mono text-xs text-slate-500">{(t.id || "").slice(0, 12)}</td>
              <td className={`p-4 text-right font-mono ${t.sign < 0 ? "text-slate-300" : "text-emerald-400"}`}>{t.sign < 0 ? "-" : "+"}{inr(t.amount)}</td>
              <td className="p-4"><StatusBadge status={t.status} /></td>
            </tr>
          ))}
          {filteredTx.length === 0 && <tr><td colSpan={5} className="p-10 text-center text-slate-500">No transactions found.</td></tr>}
        </tbody>
      </table>
    </div>
    <Pagination page={txPage} pages={txPages} onPage={setTxPage} testid="tx-pagination" />
  </div>
);

export const NotifList = ({ notifItems, notifPage, notifPages, setNotifPage }) => (
  <div className="ledger-card divide-y divide-slate-800/60 overflow-hidden rounded-2xl" data-testid="notifications-list">
    {notifItems.map((n) => (
      <div key={n.id} data-testid={`notif-row-${n.id}`} className="flex items-start gap-3 p-4">
        <div className="mt-0.5 grid h-8 w-8 flex-shrink-0 place-items-center rounded-full bg-emerald-500/15 text-emerald-400"><Bell className="h-4 w-4" /></div>
        <div className="min-w-0 flex-1">
          <p className="text-sm text-slate-200">{n.message}</p>
          <p className="mt-0.5 font-mono text-[10px] uppercase tracking-widest text-slate-500">{n.category} · {n.created_at ? new Date(n.created_at).toLocaleString("en-IN") : ""}</p>
        </div>
      </div>
    ))}
    <Pagination page={notifPage} pages={notifPages} onPage={setNotifPage} testid="notif-pagination" />
  </div>
);

export const WalletCard = ({ bal, user, totalDisplay }) => (
  <div className="cx-card-blue relative mx-auto aspect-[1.586/1] w-full max-w-sm overflow-hidden rounded-3xl border border-blue-400/20 p-6">
    <div className="pointer-events-none absolute -right-10 -top-10 h-40 w-40 rounded-full bg-white/10 blur-2xl" />
    <div className="pointer-events-none absolute -bottom-12 -left-8 h-40 w-40 rounded-full bg-amber-400/15 blur-2xl" />
    <div className="relative flex h-full flex-col justify-between">
      <div className="flex items-start justify-between">
        <div>
          <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/70">Cashback Wallet</p>
        </div>
        <img src="/paras-logo.png" alt="" className="h-10 w-10 rounded-lg object-contain" />
      </div>
      <div>
        <p className="font-mono text-[10px] uppercase tracking-widest text-white/60">Available Balance</p>
        <p data-testid="customer-wallet-available-balance" className="mt-1 font-heading text-4xl font-extrabold text-white">{bal?.available_display ?? "₹0.00"}</p>
      </div>
      <div className="flex items-end justify-between">
        <div>
          <p className="font-mono text-[9px] uppercase tracking-widest text-white/50">Card Holder</p>
          <p className="mt-0.5 font-mono text-sm uppercase tracking-wide text-white/90">{user?.name || "Paras Member"}</p>
        </div>
        <div className="text-right">
          <p className="font-mono text-[9px] uppercase tracking-widest text-white/50">Total Cashback</p>
          <p data-testid="customer-wallet-total-balance" className="mt-0.5 font-heading text-base font-bold text-white">{totalDisplay}</p>
          <span data-testid="customer-wallet-pending-balance" className="hidden">{bal?.pending_display ?? "₹0.00"}</span>
        </div>
      </div>
    </div>
  </div>
);

export const TokensGrid = ({ tokens, tokenItems, tokenPage, tokenPages, setTokenPage, setActiveToken }) => (
  <div data-testid="tokens-tab">
    <div className="mb-2 flex items-center gap-2"><Gift className="h-5 w-5 text-emerald-400" /><h2 className="font-heading text-xl font-bold">Check your tokens</h2></div>
    <p className="mb-6 text-sm text-slate-400">Each token reveals an instant cashback of ₹20/₹50/₹100 — or a Lucky Draw Ticket. The result is decided securely by our server.</p>
    {tokens.length === 0 ? (
      <EmptyState icon={Gift} testid="no-tokens" title="No tokens yet" sub="Ask a merchant to issue tokens to your mobile, then link it in the Wallet page." />
    ) : (
      <>
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
        {tokenItems.map((t) => {
          const done = t.scratched;
          const isTicket = t.result_type === "LUCKY_TICKET";
          return (
            <motion.div key={t.tokenId} whileHover={done ? {} : { scale: 1.03 }} data-testid={`token-card-${t.tokenId}`}
              className={`relative flex aspect-square flex-col items-center justify-center rounded-2xl border p-4 text-center ${done ? "border-slate-800 bg-slate-900/50" : "border-emerald-500/40 bg-gradient-to-br from-emerald-500/15 to-slate-900"}`}>
              {done ? (
                isTicket ? (
                  <>
                    <span className="text-2xl">🎟️</span>
                    <span className="mt-1 font-heading text-sm font-bold text-emerald-300">Lucky Ticket</span>
                    <span className="mt-1 text-[10px] text-slate-500">{new Date(t.created_at).toLocaleDateString("en-IN")}</span>
                  </>
                ) : (
                  <>
                    <span className="font-mono text-[10px] uppercase tracking-widest text-slate-500">Cashback</span>
                    <span className="mt-1 font-heading text-2xl font-extrabold text-emerald-400">{inr(t.cashback_paise)}</span>
                    <span className="mt-1 text-[10px] text-slate-500">{new Date(t.created_at).toLocaleDateString("en-IN")}</span>
                  </>
                )
              ) : (
                <>
                  <Sparkles className="h-7 w-7 text-emerald-300" />
                  <span className="mt-2 font-mono text-[10px] uppercase tracking-widest text-emerald-300/80">Scratch me</span>
                  <button data-testid={`scratch-btn-${t.tokenId}`} onClick={() => setActiveToken(t)} className="mt-3 rounded-full bg-emerald-500 px-4 py-1.5 text-xs font-semibold text-slate-950 hover:bg-emerald-400">Check</button>
                </>
              )}
            </motion.div>
          );
        })}
      </div>
      <Pagination page={tokenPage} pages={tokenPages} onPage={setTokenPage} testid="tokens-pagination" />
      </>
    )}
  </div>
);

export const LuckyBoard = ({ draws, luckyStats, luckyTickets, luckyItems, luckyPage, luckyPages, setLuckyPage }) => (
  <div data-testid="lucky-tab">
    <div className="mb-2 flex items-center gap-2"><Trophy className="h-5 w-5 text-amber-400" /><h2 className="font-heading text-xl font-bold">My Lucky Tickets</h2></div>
    <p className="mb-6 text-sm text-slate-400">Each ticket you earn from a scratch card is one entry in a Lucky Draw. Winners are picked securely on our server (one prize per customer). Once a ticket takes part in a draw it is permanently closed — win or not — so keep scratching new tokens for more entries.</p>

    {draws.upcoming.length > 0 && (
      <div className="mb-6 grid gap-3 sm:grid-cols-2" data-testid="upcoming-draws">
        {draws.upcoming.map((dw) => (
          <div key={dw.drawId} className="rounded-2xl border border-amber-500/30 bg-gradient-to-br from-amber-500/10 to-slate-900 p-4">
            <p className="font-mono text-[10px] uppercase tracking-widest text-amber-400/80">Upcoming draw</p>
            <p className="mt-1 font-heading text-lg font-bold">{dw.name}</p>
            {dw.description && <p className="text-sm text-slate-400">{dw.description}</p>}
            <div className="mt-2 space-y-1">
              {(dw.prize_tiers_display || []).map((t, i) => (
                <div key={i} className="flex items-center justify-between text-sm">
                  <span className="text-slate-300">{t.label}</span>
                  <span className="font-heading font-bold text-amber-400">{t.amount_display}<span className="ml-1 text-xs font-normal text-slate-500">× {t.count}</span></span>
                </div>
              ))}
            </div>
            <p className="mt-2 text-xs text-slate-500">{dw.total_winners} winner(s){dw.draw_date ? ` · Draw date: ${new Date(dw.draw_date).toLocaleDateString("en-IN")}` : ""}</p>
          </div>
        ))}
      </div>
    )}

    <div className="mb-6 grid grid-cols-2 gap-3 sm:grid-cols-5">
      <StatCard label="Total tickets" value={luckyStats?.total ?? 0} testid="lucky-total" />
      <StatCard label="Eligible" value={luckyStats?.eligible ?? 0} testid="lucky-eligible" accent />
      <StatCard label="Completed" value={luckyStats?.completed ?? 0} testid="lucky-completed" />
      <StatCard label="Winners" value={luckyStats?.winners ?? 0} testid="lucky-winners" />
      <StatCard label="Prizes won" value={inr(luckyStats?.won_prize_paise || 0)} testid="lucky-prize" accent />
    </div>

    {luckyTickets.length === 0 ? (
      <EmptyState icon={Trophy} testid="no-lucky-tickets" title="No lucky tickets yet" sub="Scratch a token — when you get a Lucky Draw Ticket it appears here." />
    ) : (
      <div className="ledger-card overflow-hidden rounded-2xl">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="lucky-tickets-table">
            <thead className="bg-slate-900/50 font-mono text-xs uppercase tracking-wider text-slate-500">
              <tr><th className="p-4 text-left">Ticket code</th><th className="p-4 text-left">Draw</th><th className="p-4 text-left">Issued</th><th className="p-4 text-left">Status</th><th className="p-4 text-right">Prize</th></tr>
            </thead>
            <tbody>
              {luckyItems.map((t) => (
                <tr key={t.ticketId} data-testid={`lucky-ticket-${t.ticketId}`} className="border-t border-slate-800/60">
                  <td className="p-4 font-mono text-xs font-bold text-emerald-400">🎟️ {t.ticketCode}</td>
                  <td className="p-4 text-slate-300">{t.draw_name}{t.draw_date ? ` · ${new Date(t.draw_date).toLocaleDateString("en-IN")}` : ""}</td>
                  <td className="p-4 text-slate-500">{new Date(t.issued_at).toLocaleDateString("en-IN")}</td>
                  <td className="p-4">
                    {t.result === "WON" ? (
                      <span className="rounded-full bg-emerald-500/15 px-2.5 py-1 text-xs font-semibold text-emerald-400">🏆 Won · Closed</span>
                    ) : t.result === "NOT_WON" ? (
                      <span className="rounded-full bg-slate-700/40 px-2.5 py-1 text-xs font-semibold text-slate-400">Not selected · Closed</span>
                    ) : (
                      <span className="rounded-full bg-amber-500/15 px-2.5 py-1 text-xs font-semibold text-amber-300">Eligible</span>
                    )}
                  </td>
                  <td className="p-4 text-right font-mono text-emerald-400">{t.prize_amount_paise ? <span>{t.prize_label ? <span className="mr-1 text-[10px] font-normal text-amber-300">{t.prize_label}</span> : null}{inr(t.prize_amount_paise)}</span> : "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <Pagination page={luckyPage} pages={luckyPages} onPage={setLuckyPage} testid="lucky-pagination" />
      </div>
    )}

    {draws.recent_winners.length > 0 && (
      <div className="mt-6 ledger-card overflow-hidden rounded-2xl" data-testid="recent-winners">
        <div className="flex items-center gap-2 border-b border-slate-800 p-5"><Trophy className="h-4 w-4 text-amber-400" /><h3 className="font-heading font-semibold">Recent Lucky Draw winners</h3></div>
        <div className="divide-y divide-slate-800/60">
          {draws.recent_winners.map((w, i) => (
            <div key={`${w.ticketCode}-${i}`} className="flex items-center justify-between p-4">
              <div>
                <p className="text-sm font-semibold">🏆 {w.winner_name}</p>
                <p className="text-xs text-slate-500">{w.draw_name}{w.prize_label ? ` · ${w.prize_label}` : ""} · 🎟️ {w.ticketCode}</p>
              </div>
              <span className="font-mono text-sm font-bold text-emerald-400">{w.prize_display}</span>
            </div>
          ))}
        </div>
      </div>
    )}
  </div>
);
